import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import SelectTree from '@app/components/select-tree/SelectTree';
import { NIL as NIL_UUID } from 'uuid';

import { messageError, messageToast } from '../../utils/define';
import { addHoTroAction } from '../../store/reducers/hotro';
import { linhVucGetAllAction } from '../../store/reducers/linhvuc';
import { khoaGetAllAction } from '../../store/reducers/khoa';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';
import { userManager } from '@app/utils/oidc';
import { getNguoiDungByAccountId } from '../../store/reducers/nguoidung';
import { LOAI_LOI_OPTIONS, MUC_DO_PHUC_TAP_OPTIONS } from '../../store/constants';

const getTodayStr = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getQueryStr = (search, key, defaultValue = '') => {
    try {
        const sp = new URLSearchParams(search || '');
        return sp.get(key) || defaultValue;
    } catch {
        return defaultValue;
    }
};

const normalizeStatusToBool = (v) => {
    if (v === true) return true;
    if (v === false) return false;

    if (typeof v === 'number') return v === 1;

    const s = String(v || '').trim().toLowerCase();
    if (!s) return false;

    if (s === '1' || s === 'true') return true;
    if (s === '0' || s === 'false') return false;

    // theo dữ liệu của bạn
    if (s === 'hoanthanh') return true;

    // còn lại coi như chưa hoàn thành
    return false;
};


// input type="date" cần YYYY-MM-DD
const toDateInputValue = (v) => {
    if (!v) return '';
    // nếu đã đúng format YYYY-MM-DD
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

    // nếu ISO string: YYYY-MM-DDTHH:mm:ss...
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);

    try {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return '';
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    } catch {
        return '';
    }
};

const schema = yup.object().shape({
    nguoiDung: yup.string().required('Vui lòng nhập Người hỗ trợ'),
    linhVucId: yup.number().required('Vui lòng chọn lĩnh vực'),
    khoaIds: yup.array().of(yup.mixed()).min(1, 'Vui lòng chọn ít nhất 1 khoa'),
    ngayHoTro: yup.string().required(messageError.emptyMessage),
    loaiLoi: yup.string().nullable(),
    mucDoPhucTap: yup.string().nullable(),
    noiDung: yup.string().nullable(),
    ghiChu: yup.string().nullable(),
});

const HoTroAdd = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const listLinhVuc = useSelector((state) => state.linhvuc.listLinhVuc || []);
    const listKhoa = useSelector((state) => state.khoa.listKhoa || []);
    const nguoiDungItem = useSelector((state) => state.nguoidung?.nguoiDungItem);

    const [isLoading, setIsLoading] = useState(false);
    const [accountId, setAccountId] = useState(null);
    const [mappedNguoiDungId, setMappedNguoiDungId] = useState(null);
    const [mappedNguoiDungName, setMappedNguoiDungName] = useState(null);
    const [accountDisplayName, setAccountDisplayName] = useState(null);
    const formikSetFieldRef = useRef(null);

    const copyId = useMemo(() => getQueryStr(location.search, 'copyId', ''), [location.search]);

    // Auto chọn lĩnh vực HIS
    const getDefaultLinhVucId = () => {
        const his = listLinhVuc.find((x) => x.name?.toLowerCase().includes('his-emr'));
        return his ? his.id : '';
    };

    // ===== initialValues state để hỗ trợ copy =====
    const [initialValues, setInitialValues] = useState(() => ({
        nguoiDung: '',
        linhVucId: '',
        khoaIds: [],
        ngayHoTro: getTodayStr(),
        loaiLoi: LOAI_LOI_OPTIONS?.[0]?.key ?? '',
        mucDoPhucTap: MUC_DO_PHUC_TAP_OPTIONS?.[0]?.key ?? '',
        noiDung: '',
        ghiChu: '',
        status: true,
    }));

    useEffect(() => {
        if (!listLinhVuc.length) dispatch(linhVucGetAllAction());
        if (!listKhoa.length) dispatch(khoaGetAllAction());
    }, [dispatch, listLinhVuc.length, listKhoa.length]);

    // ===== lấy thông tin user hiện tại =====
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const user = await userManager.getUser();
                const idFromUser = user?.profile?.sub || user?.profile?.accountId || user?.profile?.nameid || null;
                const nameFromUser = user?.profile?.name || user?.profile?.preferred_username || user?.profile?.email || null;

                if (mounted && idFromUser) {
                    setAccountId(idFromUser);
                    if (nameFromUser) setAccountDisplayName(nameFromUser);

                    try {
                        const payload = await dispatch(getNguoiDungByAccountId(idFromUser)).unwrap();
                        let item = payload || null;
                        if (Array.isArray(item)) item = item[0] || null;
                        if (item && item.data) item = item.data;
                        if (Array.isArray(item)) item = item[0] || null;
                        if (item && item.data) item = item.data;

                        const foundId = item?.id || item?.nguoiDungId;
                        const foundName = item?.hoTen || item?.fullName;

                        if (foundId) setMappedNguoiDungId(foundId);
                        if (foundName) setMappedNguoiDungName(foundName);
                    } catch { }
                    return;
                }
            } catch { }
        })();
        return () => { mounted = false; };
    }, [dispatch]);

    // ====== COPY MODE: nếu có copyId thì fill dữ liệu (trừ khoaIds) ======
    useEffect(() => {
        if (!copyId) return;

        const mapCopyItemToInitial = (src) => {
            // chỉ lấy đúng field bạn có trong form Add
            return {
                // nguoiDung vẫn để hệ thống set theo user hiện tại (readOnly)
                // nên ở đây không cần set nguoiDung

                linhVucId: src?.linhVucId ? Number(src.linhVucId) : '',
                khoaIds: [], // IMPORTANT: KHÔNG fill khoa
                ngayHoTro: getTodayStr(),
                loaiLoi: (src?.loaiLoi !== undefined && src?.loaiLoi !== null) ? String(src.loaiLoi) : (LOAI_LOI_OPTIONS?.[0]?.key ?? ''),
                mucDoPhucTap: (src?.mucDoPhucTap !== undefined && src?.mucDoPhucTap !== null) ? String(src.mucDoPhucTap) : (MUC_DO_PHUC_TAP_OPTIONS?.[0]?.key ?? ''),
                noiDung: src?.noiDung || '',
                ghiChu: src?.ghiChu || '',
                status: normalizeStatusToBool(src?.status),
            };
        };

        // ưu tiên lấy từ state, fallback sessionStorage
        const st = location.state || {};
        let src = st.copyItem || null;

        if (!src) {
            try {
                const raw = sessionStorage.getItem(`hotro_copy_${copyId}`);
                if (raw) src = JSON.parse(raw);
            } catch { }
        }

        if (src) {
            setInitialValues((prev) => ({
                ...prev,
                ...mapCopyItemToInitial(src),
            }));
        }
    }, [copyId, location.state]);

    // ===== set nguoiDung display name vào form (readOnly) =====
    useEffect(() => {
        const displayFromStore = nguoiDungItem?.hoTen || nguoiDungItem?.fullName;
        const valueToSet = mappedNguoiDungName || displayFromStore || accountDisplayName || '';

        // set vào Formik nếu đã mount
        if (formikSetFieldRef.current) {
            try { formikSetFieldRef.current('nguoiDung', valueToSet); } catch { }
        }

        // đồng thời set vào initialValues để khi Formik init lần đầu cũng có
        setInitialValues((prev) => ({
            ...prev,
            nguoiDung: valueToSet
        }));
    }, [nguoiDungItem, accountDisplayName, mappedNguoiDungName]);

    // ===== set default linhVucId (HIS) chỉ khi KHÔNG copy và hiện đang trống =====
    useEffect(() => {
        if (copyId) return; // copy thì không tự override
        setInitialValues((prev) => {
            if (prev.linhVucId) return prev;
            const def = getDefaultLinhVucId();
            return def ? { ...prev, linhVucId: def } : prev;
        });
    }, [listLinhVuc.length, copyId]);

    const handleCreate = async (values) => {
        const nguoiDungId = mappedNguoiDungId || nguoiDungItem?.id || accountId;

        const model = {
            nguoiDungId,
            linhVucId: values.linhVucId,
            khoaIds: values.khoaIds,
            ngayHoTro: values.ngayHoTro,
            loaiLoi: values.loaiLoi,
            mucDoPhucTap: values.mucDoPhucTap,
            noiDung: values.noiDung,
            ghiChu: values.ghiChu,
            status: values.status ? 1 : 0,
        };

        try {
            const result = await dispatch(addHoTroAction(model)).unwrap();
            if (result !== 200) {
                toast.error(messageToast.messageToastCreateError);
            } else {
                setIsLoading(true);
                toast.success(messageToast.messageToastCreateSuccess);

                // cleanup copy cache
                if (copyId) {
                    try { sessionStorage.removeItem(`hotro_copy_${copyId}`); } catch { }
                }

                history.goBack();
            }
        } catch (error) {
            toast.error('Đã có lỗi xảy ra, vui lòng thử lại!');
        }
    };

    return (
        <Card>
            <Card.Header as="h5">Thêm mới Hỗ Trợ</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={handleCreate}
                    initialValues={initialValues}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue, touched, submitCount, setFieldTouched }) => {
                        formikSetFieldRef.current = setFieldValue;

                        const extractIds = (arr) => {
                            if (!arr || !Array.isArray(arr)) return [];
                            return arr.map((x) => x?.id ?? x).filter(Boolean);
                        };

                        const handleSelectTreeChange = (arr) => {
                            setFieldValue('khoaIds', extractIds(arr));
                            setFieldTouched('khoaIds', true, false);
                        };

                        return (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">Người hỗ trợ</Form.Label>
                                    <Col sm="10">
                                        <Form.Control type="text" name="nguoiDung" value={values.nguoiDung} readOnly />
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">Lĩnh vực</Form.Label>
                                    <Col sm="10">
                                        <Form.Control
                                            as="select"
                                            name="linhVucId"
                                            value={values.linhVucId || ''}
                                            onChange={(e) => setFieldValue('linhVucId', Number(e.target.value))}
                                        >
                                            <option value="">-- Chọn lĩnh vực --</option>
                                            {listLinhVuc.map((lv) => (
                                                <option key={lv.id} value={lv.id}>{lv.name}</option>
                                            ))}
                                        </Form.Control>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">
                                        Khoa hỗ trợ
                                        {values.khoaIds?.length > 0 && (
                                            <span className="text-primary ms-1">({values.khoaIds.length})</span>
                                        )}
                                    </Form.Label>

                                    <Col sm="10">
                                        <SelectTree
                                            data={listKhoa.map(k => ({
                                                id: k.id,
                                                name: k.name,
                                                parentId: k.parentId || NIL_UUID
                                            }))}
                                            showSearchBox
                                            Placeholder="Chọn khoa hỗ trợ"
                                            positionExpand="right"
                                            multiSelect
                                            multiValueSelected={values.khoaIds}
                                            onChange={handleSelectTreeChange}
                                        />

                                        {(touched.khoaIds || submitCount > 0) && errors.khoaIds && (
                                            <div className="invalid-feedback d-block">{errors.khoaIds}</div>
                                        )}
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">Ngày hỗ trợ</Form.Label>
                                    <Col sm="10">
                                        <Form.Control type="date" name="ngayHoTro" value={values.ngayHoTro} onChange={handleChange} />
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">Loại lỗi</Form.Label>
                                    <Col sm="10">
                                        <Form.Control
                                            as="select"
                                            name="loaiLoi"
                                            value={values.loaiLoi}
                                            onChange={(e) => setFieldValue('loaiLoi', e.target.value)}
                                            isInvalid={!!errors.loaiLoi}
                                        >
                                            <option value="">-- Chọn loại lỗi --</option>
                                            {LOAI_LOI_OPTIONS.map(opt => (
                                                <option key={opt.key} value={opt.key}>{opt.label}</option>
                                            ))}
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">{errors.mucDo}</Form.Control.Feedback>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">Mức độ phức tạp</Form.Label>
                                    <Col sm="10">
                                        <Form.Control
                                            as="select"
                                            name="mucDoPhucTap"
                                            value={values.mucDoPhucTap}
                                            onChange={(e) => setFieldValue('mucDoPhucTap', e.target.value)}
                                            isInvalid={!!errors.mucDoPhucTap}
                                        >
                                            <option value="">-- Chọn mức độ phức tạp --</option>
                                            {MUC_DO_PHUC_TAP_OPTIONS.map(opt => (
                                                <option key={opt.key} value={opt.key}>{opt.label}</option>
                                            ))}
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">{errors.mucDo}</Form.Control.Feedback>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">Nội dung</Form.Label>
                                    <Col sm="10">
                                        <Form.Control as="textarea" rows={8} name="noiDung" value={values.noiDung} onChange={handleChange} />
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">Ghi chú</Form.Label>
                                    <Col sm="10">
                                        <Form.Control as="textarea" rows={2} name="ghiChu" value={values.ghiChu} onChange={handleChange} />
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">Hoàn thành</Form.Label>
                                    <Col sm="10">
                                        <Form.Check type="checkbox" name="status" checked={values.status} onChange={handleChange} />
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3">
                                    <Col md={{ span: 10, offset: 2 }}>
                                        <ButtonGroup>
                                            <Button type="submit" theme="primary" disabled={isLoading}>Thêm</Button>
                                            &nbsp;&nbsp;&nbsp;
                                            <Button theme="secondary" onClick={() => history.goBack()}>Hủy</Button>
                                        </ButtonGroup>
                                    </Col>
                                </Form.Group>
                            </Form>
                        );
                    }}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default HoTroAdd;
